import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import CgmModulePageLayout from './layout/CgmModulePageLayout';
import { getActiveCaisseYear, buildCaisseYearOptions } from '../utils/caisseActiveYear';
import { authJsonHeaders } from '../utils/authHeaders';
import './CgmFilterToolbar.css';
import './CgmModulePage.css';
import './NHPage.css';
import './EtatInventory.css';

export default function AddDepensePage() {
  const { activeCabinet } = useAuth();
  const cabinetLabel = activeCabinet?.is_default || Number(activeCabinet?.id) === 1 ? 'CGM1' : 'CGM2';
  const [filterType, setFilterType] = useState('jour');
  const [dateMode, setDateMode] = useState('date');
  const [selectedDate, setSelectedDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(() => getActiveCaisseYear());
  const [depenses, setDepenses] = useState([]);
  const [totalDepenses, setTotalDepenses] = useState(0);
  const [loadingDepenses, setLoadingDepenses] = useState(false);
  const [showTable, setShowTable] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const years = buildCaisseYearOptions();

  const clearResults = () => {
    setDepenses([]);
    setTotalDepenses(0);
    setShowTable(false);
  };

  const buildDepensesCgmPrintUrl = (preview = false) => {
    const params = new URLSearchParams();
    if (filterType === 'jour' && dateMode === 'periode') {
      params.set('filterType', 'periode');
      if (startDate) params.set('dateDebut', startDate);
      if (endDate) params.set('dateFin', endDate);
    } else if (filterType === 'jour') {
      params.set('filterType', 'jour');
      if (selectedDate) params.set('date', selectedDate);
    } else if (filterType === 'mois') {
      params.set('filterType', 'mois');
      params.set('mois', String(selectedMonth));
      params.set('annee', String(selectedYear));
    } else if (filterType === 'annee') {
      params.set('filterType', 'annee');
      params.set('annee', String(selectedYear));
    }
    if (preview) params.set('preview', '1');
    return `/depenses-cgm/print?${params.toString()}`;
  };

  const fetchDepenses = async () => {
    setLoadingDepenses(true);
    setError('');
    try {
      let url = '/api/depenses/bureau/par-periode';
      const params = new URLSearchParams();

      if (filterType === 'jour' && dateMode === 'periode') {
        if (startDate && endDate) {
          params.append('date_debut', startDate);
          params.append('date_fin', endDate);
        }
      } else if (filterType === 'jour' && selectedDate) {
        params.append('date_debut', selectedDate);
        params.append('date_fin', selectedDate);
      } else if (filterType === 'mois' && selectedMonth && selectedYear) {
        const start = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-01`;
        const lastDay = new Date(parseInt(selectedYear, 10), parseInt(selectedMonth, 10), 0).getDate();
        const end = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
        params.append('date_debut', start);
        params.append('date_fin', end);
      } else if (filterType === 'annee' && selectedYear) {
        params.append('date_debut', `${selectedYear}-01-01`);
        params.append('date_fin', `${selectedYear}-12-31`);
      }

      if (params.toString()) {
        url += `?${params.toString()}`;
      } else {
        url = '/api/depenses/bureau';
      }

      const response = await fetch(url, {
        headers: authJsonHeaders(),
      });
      const data = await response.json();

      if (data.success) {
        const depensesFormatees = (data.depenses || []).map((dep) => ({
          ...dep,
          libelle: dep.libelle || dep.description || '',
          description: dep.description || dep.libelle || '',
          beneficiaire: dep.beneficiaire || dep.nom_beneficiaire || dep.client || '',
          client: dep.client || dep.nom_beneficiaire || dep.beneficiaire || '',
          date: dep.date || dep.date_operation || '',
        }));
        setDepenses(depensesFormatees);
        setTotalDepenses(data.total || depensesFormatees.reduce((s, d) => s + parseFloat(d.montant || 0), 0));
        setShowTable(true);
      } else {
        setDepenses([]);
        setTotalDepenses(0);
        setShowTable(true);
        setError(data.error || 'Erreur lors du chargement des dépenses');
      }
    } catch (err) {
      console.error('Erreur lors de la récupération des dépenses:', err);
      setDepenses([]);
      setTotalDepenses(0);
      setShowTable(true);
      setError('Erreur réseau lors du chargement des dépenses');
    } finally {
      setLoadingDepenses(false);
    }
  };

  const formatMontant = (montant) => {
    const v = parseFloat(montant || 0);
    return `${v.toLocaleString('fr-FR', {
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
    })} TND`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const handleDeleteDepense = async (depenseId, beneficiaire) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer la dépense de ${beneficiaire || 'CGM'} ?`)) {
      return;
    }
    try {
      const response = await fetch(`/api/depenses/bureau/${depenseId}`, {
        method: 'DELETE',
        headers: authJsonHeaders(),
      });
      const data = await response.json();
      if (data.success) {
        setSuccess('Dépense supprimée avec succès');
        setTimeout(() => setSuccess(''), 3000);
        fetchDepenses();
        try {
          window.dispatchEvent(new CustomEvent('caisse-updated'));
        } catch (_) { /* ignore */ }
      } else {
        setError(data.error || 'Erreur lors de la suppression');
        setTimeout(() => setError(''), 3000);
      }
    } catch (_) {
      setError('Erreur lors de la suppression');
      setTimeout(() => setError(''), 3000);
    }
  };

  const searchDisabled =
    loadingDepenses ||
    (filterType === 'jour' && dateMode === 'date' && !selectedDate) ||
    (filterType === 'jour' && dateMode === 'periode' && (!startDate || !endDate));

  return (
    <CgmModulePageLayout
      crumb="Comptabilité CGM · Dépenses"
      title={`Dépenses ${cabinetLabel}`}
      backTo="/"
      wide
    >
      {error && (
        <div className="alert alert-danger" role="alert">{error}</div>
      )}
      {success && (
        <div className="alert alert-success" role="alert">{success}</div>
      )}

      <div className="cgm-list-toolbar">
        <div className="cgm-filter-panel cgm-period-panel">
          <div className="cgm-filter-panel__title">Filtrer les dépenses {cabinetLabel}</div>
          <div className="cgm-period-body">
            <div className="cgm-period-tabs">
              <button
                type="button"
                className={`cgm-period-tab ${filterType === 'jour' ? 'active' : ''}`}
                onClick={() => {
                  setFilterType('jour');
                  clearResults();
                }}
              >
                Jour
              </button>
              <button
                type="button"
                className={`cgm-period-tab ${filterType === 'mois' ? 'active' : ''}`}
                onClick={() => {
                  setFilterType('mois');
                  clearResults();
                }}
              >
                Mois
              </button>
              <button
                type="button"
                className={`cgm-period-tab ${filterType === 'annee' ? 'active' : ''}`}
                onClick={() => {
                  setFilterType('annee');
                  clearResults();
                }}
              >
                Année
              </button>
            </div>

            {filterType === 'jour' && (
              <div className="cgm-period-jour-panel">
                <div className="cgm-period-mode-group" role="group" aria-label="Mode jour">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      id="cgmDepJourDate"
                      name="cgmDepJourMode"
                      checked={dateMode === 'date'}
                      onChange={() => {
                        setDateMode('date');
                        clearResults();
                      }}
                    />
                    <label className="form-check-label" htmlFor="cgmDepJourDate">Par date</label>
                  </div>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      id="cgmDepJourPeriode"
                      name="cgmDepJourMode"
                      checked={dateMode === 'periode'}
                      onChange={() => {
                        setDateMode('periode');
                        clearResults();
                      }}
                    />
                    <label className="form-check-label" htmlFor="cgmDepJourPeriode">Par période</label>
                  </div>
                </div>
                <div className="cgm-period-filter-row">
                  {dateMode === 'date' ? (
                    <>
                      <label className="mb-0">Date</label>
                      <input
                        type="date"
                        className="hono-input-date"
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                      />
                    </>
                  ) : (
                    <>
                      <label className="mb-0">De</label>
                      <input
                        type="date"
                        className="hono-input-date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                      />
                      <label className="mb-0">à</label>
                      <input
                        type="date"
                        className="hono-input-date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                      />
                    </>
                  )}
                </div>
              </div>
            )}

            {filterType === 'mois' && (
              <div className="cgm-period-filter-row">
                <label className="mb-0">Mois</label>
                <select
                  className="hono-select-month"
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(Number(e.target.value))}
                >
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                    <option key={m} value={m}>{String(m).padStart(2, '0')}</option>
                  ))}
                </select>
                <label className="mb-0">Année</label>
                <select
                  className="hono-select-year"
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(Number(e.target.value))}
                >
                  {years.map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
            )}

            {filterType === 'annee' && (
              <div className="cgm-period-filter-row">
                <label className="mb-0">Année</label>
                <select
                  className="hono-select-year"
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(Number(e.target.value))}
                >
                  {years.map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="cgm-period-actions">
              <button
                type="button"
                className="cgm-toolbar-btn cgm-toolbar-btn--primary"
                onClick={fetchDepenses}
                disabled={searchDisabled}
              >
                {loadingDepenses ? 'Recherche…' : 'Rechercher'}
              </button>
              <button
                type="button"
                className="cgm-toolbar-btn cgm-toolbar-btn--voir"
                onClick={() => window.open(buildDepensesCgmPrintUrl(true), '_blank')}
                title="Visionner avant impression"
              >
                Visionnage
              </button>
              <button
                type="button"
                className="cgm-toolbar-btn cgm-toolbar-btn--print"
                onClick={() => window.open(buildDepensesCgmPrintUrl(), '_blank')}
                title="Imprimer la liste"
              >
                Imprimer
              </button>
            </div>
          </div>
        </div>
      </div>

      {loadingDepenses && (
        <div className="text-center my-4">
          <div className="spinner-border" style={{ color: '#0f766e' }} role="status">
            <span className="visually-hidden">Chargement…</span>
          </div>
        </div>
      )}

      {!loadingDepenses && showTable && depenses.length > 0 && (
        <div className="nh-modern-table-card nh-modern-table-card--depenses-cgm">
          <div className="nh-modern-table-toolbar">
            <div className="nh-modern-table-info">
              {depenses.length} dépense{depenses.length > 1 ? 's' : ''} · Total{' '}
              <strong style={{ color: '#dc2626' }}>{formatMontant(totalDepenses)}</strong>
            </div>
          </div>
          <div className="overview-table-container nh-modern-table-scroll">
            <table className="table overview-table nh-overview-data-table nh-modern-data-table">
              <thead>
                <tr>
                  <th><span className="nh-modern-th-inner">Date</span></th>
                  <th><span className="nh-modern-th-inner">Client</span></th>
                  <th><span className="nh-modern-th-inner">Libellé</span></th>
                  <th><span className="nh-modern-th-inner">Montant</span></th>
                  <th><span className="nh-modern-th-inner">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {depenses.map((depense, index) => {
                  const rawText = (depense.libelle || depense.description || '').toUpperCase();
                  const isCgmDepense = rawText.includes('[CGM]') || rawText.includes('HONORAIRES REÇU');
                  const clientName = isCgmDepense
                    ? (depense.nom_beneficiaire || depense.beneficiaire || depense.client)
                    : (depense.beneficiaire || depense.client);
                  let libelleText = depense.libelle || depense.description || '—';
                  libelleText = libelleText.replace(/^\[CGM\]\s*/i, '[PAYÉ PAR CGM] ');
                  const isHonoraire =
                    rawText.includes('HONORAIRES REÇU') ||
                    rawText.includes('HONORAIRES RECU') ||
                    rawText.includes('AVANCE');

                  return (
                    <tr key={depense.id || index}>
                      <td>{formatDate(depense.date || depense.date_operation)}</td>
                      <td>{(clientName || '—').toString().toUpperCase()}</td>
                      <td>{String(libelleText).toUpperCase()}</td>
                      <td className="text-end">
                        <span
                          className="nh-badge-amount"
                          style={{ color: isHonoraire ? '#059669' : '#dc2626' }}
                        >
                          {formatMontant(depense.montant)}
                        </span>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="cgm-toolbar-btn cgm-toolbar-btn--delete"
                          onClick={() => handleDeleteDepense(depense.id, depense.beneficiaire || depense.client)}
                          title="Supprimer"
                          style={{ padding: '0.25rem 0.55rem', fontSize: '0.75rem' }}
                        >
                          Supprimer
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={3}><strong>TOTAL</strong></td>
                  <td className="text-end"><strong style={{ color: '#dc2626' }}>{formatMontant(totalDepenses)}</strong></td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {!loadingDepenses && showTable && depenses.length === 0 && !error && (
        <div className="text-center text-muted py-4">
          Aucune dépense pour ces critères.
        </div>
      )}

      {!loadingDepenses && !showTable && (
        <div className="text-center text-muted py-4">
          Choisissez une période puis cliquez sur Rechercher.
        </div>
      )}
    </CgmModulePageLayout>
  );
}
